import React from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Typography
} from "@mui/material/";
import { useState } from "react";
import actions from "../../Store/actions";
import { strings } from "../../common/i18n";
import { logToSentry } from "../../config/Sentry";

export default ({ open, onClose, row, setLoading, fetchAudits }) => {
    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);

    const rows = Array.isArray(row) ? row : row ? [row] : [];
    const isBulk = rows.length > 1;

    const handleConfirm = async () => {
        if (!rows.length) return;
        setSubmitting(true);
        try {
            await dispatch(
                actions.audits.create.bulkAbandonedCloseRequest(
                    rows.map((r) => r.id)
                )
            );
            dispatch(
                actions.toast.create.success(strings("messages.auditAbandoned"))
            );
            if (setLoading) setLoading(true);
            await fetchAudits();
            setSubmitting(false);
            onClose();
        } catch (error) {
            logToSentry("On abandon audit from status table:", { error });
            // A 207 from the API means some ids failed. If others succeeded the
            // list is now stale, so refresh it and say what actually happened
            // rather than reporting a blanket failure.
            const succeeded = error?.succeeded ?? [];
            const failed = error?.failed ?? [];
            if (succeeded.length > 0) {
                dispatch(
                    actions.toast.create.warning(
                        strings("errors.onAbandonAuditPartial", {
                            succeeded: succeeded.length,
                            failed: failed.length
                        })
                    )
                );
                if (setLoading) setLoading(true);
                await fetchAudits();
                setSubmitting(false);
                onClose();
                return;
            }
            dispatch(
                actions.toast.create.error(strings("errors.onAbandonAudit"))
            );
            setSubmitting(false);
        }
    };

    return (
        rows.length > 0 && (
            <Dialog
                open={open}
                onClose={() => !submitting && onClose()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{strings("abandonAudit")}</DialogTitle>
                <Divider />
                <DialogContent>
                    <DialogContentText>
                        {strings("messages.abandonAuditConfirm")}
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 1 }}>
                        {strings("messages.abandonAuditWarning")}
                    </DialogContentText>
                    {isBulk && (
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                                gutterBottom
                            >
                                {`${rows.length} audits will be abandoned:`}
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 160,
                                    overflowY: "auto",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1
                                }}
                            >
                                <List dense disablePadding>
                                    {rows.map((r, i) => (
                                        <ListItem
                                            key={r.id}
                                            divider={i < rows.length - 1}
                                            sx={{ py: 0.75, px: 1.5 }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2">
                                                        {r.name}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={submitting}
                        startIcon={
                            submitting ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                    >
                        {submitting
                            ? strings("abandoning")
                            : strings("confirmAbandon")}
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        {strings("cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    );
};
